import { type FC } from "react";

import { type OptionItem, OptionType, useGetOptionsQuery } from "@/api/admin";
import type {
  SettlementTransfer,
  SettlementTransfersListResponse,
} from "@/api/admin/settlementTransfers";
import {
  Loader,
  TableCellText,
  TableHeadCellCustom,
  type TableHeaderType,
} from "@/components/atoms";
import {
  DropDownViewMenu,
  FilterSearch,
  type SubMenuConfig,
} from "@/components/molecules";
import type { SubMenuItem } from "@/components/molecules/FilterSearch/DropDownFilterMenu";
import { formatIsoDate } from "@/utils/formatIsoToUTC";
import { useAdminMoney } from "@/utils/hooks/useAdminMoney";
import { type IGroupParams } from "@/utils/hooks/useTableParams";
import { useVisibleFlags } from "@/utils/hooks/useVisibleFlags";
import { parseDateRangeToParams } from "@/utils/parseDateRangeToParams";

import DynamicTable from "../DynamicTable";
import { type ColumnConfig, isOptionItem } from "../type";

enum TableColumn {
  TRANSFER_DATE = "Transfer Date",
  ENTITY_NAME = "Group Name",
  REFERRENCE_NUMBER = "Group Reference Number",
  LEGAL_ENTITY = "Legal Entity",
  ENTITY_TYPE = "Entity Type",
  TRANSFER_AMOUNT = "Transfer Amount",
  BANK_TRANSF_ID = "Bank Transfer ID",
}

enum FilterEntity {
  GROUP = "Group",
  BRAND = "Brand",
  BRANCH = "Branch",
  LEGAL = "Legal entity",
}

const TABLE_HEADERS: TableHeaderType = Object.values(TableColumn);

const SUB_MENU_REGISTRY: Record<string, SubMenuConfig> = {
  [FilterEntity.GROUP]: {
    type: "search",
    placeholder: "Search group",
    isSingleSelect: true,
  },
  [FilterEntity.LEGAL]: {
    type: "search",
    placeholder: "Search legal entity",
    isSingleSelect: true,
  },
  [TableColumn.TRANSFER_DATE]: { type: "date" },
};

interface TransactionsTableProps {
  tableData?: SettlementTransfersListResponse;
  tableParams: IGroupParams;
  setTableParams: React.Dispatch<React.SetStateAction<IGroupParams>>;
  handleNext: () => void;
  handlePrev: () => void;
  searchValue: string;
  setSearchValue: (val: string) => void;
  isPending: boolean;
}

const AdminSettlementTransfersReportTable: FC<TransactionsTableProps> = ({
  tableData,
  setTableParams,
  handleNext,
  handlePrev,
  searchValue,
  setSearchValue,
  isPending,
}) => {
  const { data: optionsData, isPending: isOptionPending } = useGetOptionsQuery([
    OptionType.LegalEntities,
    OptionType.Groups,
  ]);

  const SUB_MENUS: Record<string, SubMenuItem[]> = {
    [FilterEntity.GROUP]: optionsData?.groups ?? [],
    [FilterEntity.LEGAL]: optionsData?.legalEntities ?? [],
    [TableColumn.TRANSFER_DATE]: [],
  };

  const MENU_LIST = Object.keys(SUB_MENUS);

  const handleSetFilter = (
    selectedMap: Record<string, string[] | OptionItem[]>
  ) => {
    const { dateFrom: transferDateFrom, dateTo: transferDateTo } =
      parseDateRangeToParams(selectedMap[TableColumn.TRANSFER_DATE]?.[0], true);

    const selectedGroupName = selectedMap[FilterEntity.GROUP]?.[0] ?? "";
    const selectedLegalEntity = selectedMap[FilterEntity.LEGAL]?.[0] ?? "";

    setTableParams(prev => ({
      ...prev,
      groupId: isOptionItem(selectedGroupName)
        ? selectedGroupName.id
        : undefined,
      legalEntityId: isOptionItem(selectedLegalEntity)
        ? selectedLegalEntity.id
        : undefined,
      transferDateFrom,
      transferDateTo,
      offset: 0,
    }));
  };

  const { visibleColumns, setVisibleColumns, flags } =
    useVisibleFlags(TABLE_HEADERS);

  const money = useAdminMoney();

  const columns: ColumnConfig<SettlementTransfer>[] = [
    {
      key: "transferDate",
      label: TableColumn.TRANSFER_DATE,
      visible: flags[TableColumn.TRANSFER_DATE],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {formatIsoDate(row?.transferDate) || "-"}
        </TableCellText>
      ),
    },
    {
      key: "groupName",
      label: TableColumn.ENTITY_NAME,
      visible: flags[TableColumn.ENTITY_NAME],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>{row.groupName ?? "-"}</TableCellText>
      ),
    },
    {
      key: "groupReferenceNumber",
      label: TableColumn.REFERRENCE_NUMBER,
      visible: flags[TableColumn.REFERRENCE_NUMBER],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {row?.groupReferenceNumber ?? "-"}
        </TableCellText>
      ),
    },
    {
      key: "legalEntityName",
      label: TableColumn.LEGAL_ENTITY,
      visible: flags[TableColumn.LEGAL_ENTITY],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {row?.legalEntityName ?? "-"}
        </TableCellText>
      ),
    },
    {
      key: "transferAmount",
      label: TableColumn.TRANSFER_AMOUNT,
      visible: flags[TableColumn.TRANSFER_AMOUNT],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {money.render(row.transferAmount?.toString(), "", true)}
        </TableCellText>
      ),
    },
    {
      key: "bankTransferId",
      label: TableColumn.BANK_TRANSF_ID,
      visible: flags[TableColumn.BANK_TRANSF_ID],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {row?.bankTransferId || "-"}
        </TableCellText>
      ),
    },
  ];

  return (
    <>
      <FilterSearch
        menuList={MENU_LIST}
        subMenus={SUB_MENUS}
        subMenuRegistry={SUB_MENU_REGISTRY}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        handleSetFilter={handleSetFilter}
      >
        <DropDownViewMenu
          viewList={TABLE_HEADERS}
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
        />
      </FilterSearch>

      {isPending || isOptionPending ? (
        <Loader />
      ) : (
        <DynamicTable<SettlementTransfer>
          data={tableData?.items || []}
          columns={columns}
          searchValue={searchValue}
          next={handleNext}
          prev={handlePrev}
          tableParams={tableData}
          setTableParams={setTableParams}
        />
      )}
    </>
  );
};

export default AdminSettlementTransfersReportTable;
